import React, { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { ContentCopy as ContentCopyIcon, Image as ImageIcon } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import apiService from "../services/api";
import {
  downloadProgramAssetPng,
  useProgramAssetAutoDownload,
} from "../utils/programAssetPngExport";
import LinkedInLivestreamCompareAsset, {
  LINKEDIN_ASSET_HEIGHT_PX,
  LINKEDIN_ASSET_WIDTH_PX,
} from "../components/linkedin-asset/LinkedInLivestreamCompareAsset";
import LinkedInBrandRadiographyAsset from "../components/linkedin-asset/LinkedInBrandRadiographyAsset";
import LinkedInLivestreamBrandsAsset, {
  buildLivestreamBrandsRawMetricsText,
} from "../components/linkedin-asset/LinkedInLivestreamBrandsAsset";
import LinkedInDigitalInvestMapAsset from "../components/linkedin-asset/LinkedInDigitalInvestMapAsset";
import LinkedInShareDayAsset, {
  buildShareDayRawDataText,
} from "../components/linkedin-asset/LinkedInShareDayAsset";
import {
  DIGITAL_INVEST_WEEK_END,
  DIGITAL_INVEST_WEEK_START,
} from "../components/linkedin-asset/linkedinDigitalInvestMap";
import { PROGRAM_ANALYSIS_ROUTE_TAXONOMY } from "../constants/programAnalysisTaxonomy";
import AssetPreviewStage from "../components/asset-preview/AssetPreviewStage";
import { waitAssetPreviewPaint } from "../components/asset-preview/assetPreviewViewport";

const PAGE_BG = "#1a1a1a";
/** Vértigo | El react de las Amikas | Capítulo 3 — 29 oct 2026, El 13. */
const DEFAULT_LIVESTREAM_BRANDS_ID = "6ae3dc7b58951c7e4e599857";
/** Previous marcas-vivo default (React Fiebre de Canto). The standing URL still passes it. */
const PREVIOUS_LIVESTREAM_BRANDS_ID = "6adea6372812a791cb37baa2";

const ProgramAnalysisLinkedInAssetRender: React.FC = () => {
  const [searchParams] = useSearchParams();
  const cardRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [metricsCopied, setMetricsCopied] = useState(false);
  const shouldAutoDownload = searchParams.get("download") === "1";
  const showChatViewers = searchParams.get("chat") === "1";
  const asset = searchParams.get("asset");
  const isRadiografia = asset === "radiografia";
  const isInvestMap = asset === "donde-invertir";
  const isLivestreamBrands = asset === "marcas-vivo";
  const isShareDay = asset === "share-day";
  const ids = (searchParams.get("ids") ?? "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
  const requestedBrandId = searchParams.get("id")?.trim() || ids[0] || "";
  const livestreamBrandId =
    !requestedBrandId || requestedBrandId === PREVIOUS_LIVESTREAM_BRANDS_ID
      ? DEFAULT_LIVESTREAM_BRANDS_ID
      : requestedBrandId;

  const compareQuery = useQuery({
    queryKey: ["linkedin-livestream-compare", "last-night", ids.join(",")],
    queryFn: () => apiService.getLinkedInLivestreamCompare(ids, "last"),
    enabled:
      !isRadiografia &&
      !isInvestMap &&
      !isLivestreamBrands &&
      !isShareDay &&
      ids.length >= 2,
  });

  const livestreamBrandsQuery = useQuery({
    queryKey: ["linkedin-livestream-brands", livestreamBrandId],
    queryFn: () => apiService.getLinkedInLivestreamBrands(livestreamBrandId),
    enabled: isLivestreamBrands && Boolean(livestreamBrandId),
  });

  const radiografiaQuery = useQuery({
    queryKey: ["linkedin-brand-radiography", "bci-2026-10-26", "views-2026-11-04", "betterplan-bci-1001", "df-no-truck"],
    queryFn: () => apiService.getLinkedInBrandRadiography(),
    enabled: isRadiografia,
  });

  const shareDayQuery = useQuery({
    queryKey: ["linkedin-share-day", "2026-11-02", "tv"],
    queryFn: () => apiService.getLinkedInShareDay(),
    enabled: isShareDay,
  });

  const investProgramsQuery = useQuery({
    queryKey: ["linkedin-digital-invest-programs", DIGITAL_INVEST_WEEK_START],
    queryFn: () =>
      apiService.getProgramsAnalysis(
        DIGITAL_INVEST_WEEK_START,
        PROGRAM_ANALYSIS_ROUTE_TAXONOMY,
        undefined,
        "avgViewsDay7",
        "desc",
        undefined,
        undefined,
        true,
      ),
    enabled: isInvestMap,
  });

  const investLiveQuery = useQuery({
    queryKey: [
      "linkedin-digital-invest-live",
      DIGITAL_INVEST_WEEK_START,
      DIGITAL_INVEST_WEEK_END,
    ],
    queryFn: () =>
      apiService.getExplorerLivestreams(
        DIGITAL_INVEST_WEEK_START,
        DIGITAL_INVEST_WEEK_END,
        "all",
        "digital",
        {
          ranking: {
            page: 0,
            pageSize: 100,
            mode: "programs",
            sortColumn: "avgViewers",
            sortDirection: "desc",
          },
        },
      ),
    enabled: isInvestMap,
  });

  useEffect(() => {
    if (!isRadiografia || !radiografiaQuery.data?.debug) return;
    const { debug } = radiografiaQuery.data;
    console.log("[Radiografía de marca] mentions before exclusion:", debug.mentionsBeforeExclusion);
    console.log("[Radiografía de marca] mentions after exclusion:", debug.mentionsAfterExclusion);
    console.log("[Radiografía de marca] excluded ids:", debug.excludedIds);
    console.log("[Radiografía de marca] per-channel counts:", debug.perChannelCounts);
  }, [isRadiografia, radiografiaQuery.data]);

  const streams = compareQuery.data?.streams ?? [];
  const investPrograms = investProgramsQuery.data?.programs ?? [];
  const investLiveRows = investLiveQuery.data?.ranking?.programRows ?? [];
  const pngFilename = isRadiografia
    ? "linkedin-radiografia-bci.png"
    : isInvestMap
      ? "linkedin-donde-invertir-digital.png"
      : isLivestreamBrands
        ? "linkedin-livestream-marcas.png"
        : isShareDay
          ? "linkedin-share-day-tv.png"
          : "linkedin-livestream-compare.png";
  const assetHeightPx = LINKEDIN_ASSET_HEIGHT_PX;
  const isReady = isRadiografia
    ? !radiografiaQuery.isLoading && Boolean(radiografiaQuery.data?.lanes?.length)
    : isInvestMap
      ? !investProgramsQuery.isLoading &&
        !investLiveQuery.isLoading &&
        investPrograms.length > 0
      : isLivestreamBrands
        ? !livestreamBrandsQuery.isLoading && Boolean(livestreamBrandsQuery.data?.stream)
        : isShareDay
          ? !shareDayQuery.isLoading &&
            Boolean(shareDayQuery.data?.series?.length) &&
            Boolean(shareDayQuery.data?.ranking?.length)
          : !compareQuery.isLoading && streams.length >= 2;

  useProgramAssetAutoDownload(cardRef, shouldAutoDownload, isReady, pngFilename, {
    width: LINKEDIN_ASSET_WIDTH_PX,
    height: assetHeightPx,
    backgroundColor: PAGE_BG,
  });

  const handleDownloadPng = async () => {
    flushSync(() => setIsDownloading(true));
    await waitAssetPreviewPaint();
    try {
      await downloadProgramAssetPng(cardRef.current, pngFilename, {
        width: LINKEDIN_ASSET_WIDTH_PX,
        height: assetHeightPx,
        backgroundColor: PAGE_BG,
      });
    } catch (err) {
      console.error("LinkedIn asset PNG download failed:", err);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCopyRawMetrics = async () => {
    const text = isShareDay
      ? shareDayQuery.data
        ? buildShareDayRawDataText(shareDayQuery.data)
        : null
      : livestreamBrandsQuery.data
        ? buildLivestreamBrandsRawMetricsText(livestreamBrandsQuery.data)
        : null;
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setMetricsCopied(true);
      window.setTimeout(() => setMetricsCopied(false), 2000);
    } catch (err) {
      console.error("LinkedIn asset raw metrics copy failed:", err);
    }
  };

  if (isLivestreamBrands && !livestreamBrandId) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Falta el id del livestream.</Typography>
      </Box>
    );
  }

  if (
    !isRadiografia &&
    !isInvestMap &&
    !isLivestreamBrands &&
    !isShareDay &&
    ids.length < 2
  ) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Faltan los ids de los livestreams.</Typography>
      </Box>
    );
  }

  if (
    isRadiografia
      ? radiografiaQuery.isLoading
      : isInvestMap
        ? investProgramsQuery.isLoading || investLiveQuery.isLoading
        : isLivestreamBrands
          ? livestreamBrandsQuery.isLoading
          : isShareDay
            ? shareDayQuery.isLoading
            : compareQuery.isLoading
  ) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isReady) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>
          {isRadiografia
            ? "No se pudieron cargar las menciones de marca."
            : isInvestMap
              ? "No se pudieron cargar los programas digitales."
              : isLivestreamBrands
                ? "No se pudieron cargar las marcas de ese livestream."
                : isShareDay
                  ? "No se pudo cargar el share del día."
                  : "No se pudieron cargar esos livestreams."}
        </Typography>
      </Box>
    );
  }

  return (
    <AssetPreviewStage
      widthPx={LINKEDIN_ASSET_WIDTH_PX}
      heightPx={assetHeightPx}
      disabled={shouldAutoDownload || isDownloading}
      toolbarExtra={
        <Box
          sx={{
            position: "fixed",
            top: 8,
            right: 8,
            zIndex: 12,
            display: "flex",
            gap: 1,
          }}
        >
          {isLivestreamBrands || isShareDay ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={() => void handleCopyRawMetrics()}
              disabled={
                isShareDay
                  ? !shareDayQuery.data
                  : !livestreamBrandsQuery.data
              }
              sx={{ bgcolor: "background.paper" }}
            >
              {metricsCopied
                ? "Copied!"
                : isShareDay
                  ? "Copy graph data"
                  : "Copy raw metrics"}
            </Button>
          ) : null}
          {process.env.NODE_ENV === "development" ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={isDownloading ? <CircularProgress size={16} /> : <ImageIcon />}
              onClick={handleDownloadPng}
              disabled={isDownloading}
              sx={{ bgcolor: "background.paper" }}
            >
              {isDownloading ? "Generando…" : "Download PNG"}
            </Button>
          ) : null}
        </Box>
      }
    >
      <Box ref={cardRef}>
        {isRadiografia && radiografiaQuery.data ? (
          <LinkedInBrandRadiographyAsset data={radiografiaQuery.data} />
        ) : isShareDay && shareDayQuery.data ? (
          <LinkedInShareDayAsset data={shareDayQuery.data} />
        ) : isLivestreamBrands && livestreamBrandsQuery.data ? (
          <LinkedInLivestreamBrandsAsset data={livestreamBrandsQuery.data} />
        ) : isInvestMap ? (
          <LinkedInDigitalInvestMapAsset
            programs={investPrograms}
            livestreamRows={investLiveRows}
            trackedProgramCount={
              investProgramsQuery.data?.activeProgramCount ??
              investPrograms.length
            }
          />
        ) : (
          <LinkedInLivestreamCompareAsset
            streams={streams}
            showChatViewers={showChatViewers}
          />
        )}
      </Box>
    </AssetPreviewStage>
  );
};

export default ProgramAnalysisLinkedInAssetRender;
